import Vue from 'vue';
import { isTablet, intersectionObserver } from 'utils/general';
import { trackEvent, trackCTA } from 'lib/tracking';
import { A11y, Autoplay, Pagination, Keyboard } from 'swiper/modules';
import VimeoHelper from "lib/vimeoHelper";

export default (el) =>
  new Vue({
    components: {
      'vue-swiper':  () => import('../vue-swiper/vue-swiper.vue'),
    },
    el,
    data() {
      return {
        componentName: document.body.dataset.origin + '/' + el.dataset.componentName,
        siteSection: el.dataset.siteSection,
        isTablet: isTablet(),
        video: null,
        isPaused: false,
        isMuted: true,
        frame: null,
        vimeoPlayers: [],
        swiper: null,
      }
    },
    computed: {
      swiperOptions() {
        const _this = this;

        let option = {
          loop: true,
          grabCursor: true,
          pagination: {
            el: '.swiper-pagination',
            clickable: true,
            renderBullet: function (index, className) {
              return `<button class="${className}"></button>`;
            },
          },
          a11y: true,
          keyboard: true,
          modules: [Pagination, A11y, Keyboard],
          on: {
            init: (swiper) => {
              _this.swiper = swiper;

              const { carousel } = _this.$refs;

              if (carousel) {
                _this.setVideoData();
              }
              _this.bannersViewTracking();
            },
            slideChange: (swiper) => {
              const currentSlide = swiper.slides[swiper.activeIndex];
              const mobileId = currentSlide.getAttribute('data-mobile-id');
              const desktopId = currentSlide.getAttribute('data-desktop-id');

              _this.vimeoPlayers.forEach((player) => {
                if (
                  (_this.isTablet && player.mobileId === mobileId) ||
                  (!_this.isTablet && player.desktopId === desktopId)
                ) {
                  player.player.play();
                  _this.video = player.player;
                  _this.isPaused = false;
                } else {
                  player.player.pause();
                  player.player.setVolume(0);
                  _this.isMuted = true;
                }
              });
              _this.placeSliderDots(currentSlide);
            }
          }
        };

        const { delay } = this.$el.dataset;
        if (delay > 0) {
          option = {
            ...option,
            autoplay: {
              delay: delay
            }
          }
          option.modules.push(Autoplay)
        }

        return option;
      }
    },
    mounted() {
      if (this.$el.dataset.enableTracking === 'true') {
        this.payload = {
          banner_id: this.$el.dataset.templateId,
          banner_location: this.$el.dataset.module,
          ...(this.siteSection && { site_section: this.siteSection })
        };
      }
    },
    methods: {
      async checkAndSetAudioAttribute(slide, videoId) {
        try {
          const videoMetadata = await VimeoHelper.getVideoMetadata(videoId);
          const hasAudio = videoMetadata?.has_audio ?? false;
          slide.setAttribute('data-has-audio', hasAudio);
        } catch (error) {
          console.error('Error checking audio for video:', videoId, error);
        }
      },
      setVideoData() {
        const slides = this.swiper.slides;

        slides.forEach((slide, index) => {
          const iframe = slide.querySelector('iframe');
          if (iframe) {
            const mobileId = slide.getAttribute('data-mobile-id');
            const desktopId = slide.getAttribute('data-desktop-id');
            const mobileVideoId = mobileId.split('-')[0];
            const desktopVideoId = desktopId.split('-')[0];

            if (this.isTablet && mobileId && index !== 0) {
              iframe.src = `https://player.vimeo.com/video/${mobileVideoId}?background=1`;
            }
            if (!this.isTablet && desktopId && index !== 0) {
              iframe.src = `https://player.vimeo.com/video/${desktopVideoId}?background=1`;
            }

            const video = new Vimeo.Player(iframe);
            const playerExists = this.vimeoPlayers.find(
              (player) =>
                (this.isTablet && player.mobileId === mobileId) ||
                (!this.isTablet && player.desktopId === desktopId),
            );
            if (!playerExists) {
              this.vimeoPlayers.push({ mobileId, desktopId, player: video });
            }

            if (this.swiper.activeIndex === index) {
              this.video = video;
              this.placeSliderDots(slide);
            }

            const videoId = this.isTablet ? mobileVideoId : desktopVideoId;

            // Call the async function but don't wait for it to complete
            this.checkAndSetAudioAttribute(slide, videoId);
          }
        });
      },
      handleSwiperSlideChange(swiper) {
        const currentSlide = swiper.slides[swiper.activeIndex];
        const mobileId = currentSlide.getAttribute('data-mobile-id');
        const desktopId = currentSlide.getAttribute('data-desktop-id');

        this.vimeoPlayers.forEach((player) => {
          if (
            (this.isTablet && player.mobileId === mobileId) ||
            (!this.isTablet && player.desktopId === desktopId)
          ) {
            player.player.play();
            this.video = player.player;
            this.isPaused = false;
          } else {
            player.player.pause();
            player.player.setVolume(0);
            this.isMuted = true;
          }
        });
        this.placeSliderDots(currentSlide);
      },
      bannersViewTracking() {
        if (this.$el.dataset.enableTracking === 'true') {
          const bannerChildren = this.$el.querySelectorAll('[data-hero-item]');
          bannerChildren.forEach((banner) => {
            intersectionObserver(banner, () =>
              trackEvent({
                event: 'banner_impression',
                ...this.payload,
                banner_name:
                  banner.dataset.heroItem !== ''
                    ? banner.dataset.heroItem
                    : undefined,
                shopify_module_name: 'hero video slider'
              }),
            );
          });
        }
      },
      clickTracking(url, banner_name = undefined, cta_text = undefined) {
        if (this.$el.dataset.enableTracking === 'true') {
          trackEvent({
            event: 'banner_click',
            ...this.payload,
            banner_name,
            shopify_module_name: 'hero video slider'
          });
        } else window.location.href = url;
      },
      async handlePauseClick() {
        this.isPaused ? this.playVideo() : this.pauseVideo();
        this.isPaused = !this.isPaused;

        Promise.all([this.video.getDuration(), this.video.getVideoTitle()]).then(values => {
          trackEvent({
            event: 'video_engagement',
            video_name: values[1],
            video_duration: values[0],
            component_name: this.componentName,
            engagement_type: this.isPaused ? 'pause' : 'play',
            ...(this.siteSection && { site_section: this.siteSection })
          })
        })
      },
      handleSoundClick() {
        this.isMuted ? this.video.setVolume(1) : this.video.setVolume(0);
        this.isMuted = !this.isMuted;
      },
      placeSliderDots(slide) {
        const dots = this.$el.querySelector('.swiper-pagination');
        if (!dots) return;
        if (!this.isTablet) {
          const position = slide.getAttribute('data-video-position');
          dots.style.left = position === 'left' ? '50%' : '0';
        } else {
          dots.style.left = 'unset';
        }
      },
      async playVideo() {
        if (this.isPaused) {
          return this.video.play();
        }
      },
      pauseVideo() {
        if (!this.isPaused) {
          return this.video.pause();
        }
      },
    },
  });
